import React, { useId } from "react";
import { Tooltip } from "devextreme-react/tooltip";
import { PhaseWorkflowType } from "../../dtos/PhaseWorkflowType";
import { WORKFLOW_TYPE_LABELS, useWorkflowTypeDescriptions } from "./useWorkflowTypeInfo";

type Props = {
    workflowType?: PhaseWorkflowType | null;
    // Prefix shown before the type name (e.g. "Workflow" or "Subtask Workflow").
    prefix?: string;
    // Optional placeholder when the type is not yet known (e.g. new subtask under a hybrid parent).
    placeholder?: string;
};

// Readonly chip displaying the workflow type for a task or subtask. Shown at the top of
// the config editor so the type is always visible while configuring settings on any tab.
// Hovering the chip opens a Tooltip with the same description shown in the workflow type picker.
export default function WorkflowTypeBadge({ workflowType, prefix = "Workflow", placeholder }: Props) {
    const descriptions = useWorkflowTypeDescriptions();

    // Stable per-instance id so the Tooltip attaches to the correct chip when several
    // badges render on the same page.
    const reactId = useId();
    const tooltipTargetId = `workflow-type-badge-${reactId.replace(/[:]/g, "")}`;

    const label = workflowType != null ? WORKFLOW_TYPE_LABELS[workflowType] : null;
    const display = label ?? placeholder;
    if (!display) return null;

    const description = workflowType != null ? descriptions.get(workflowType) : undefined;
    const lockNote = `${prefix} type is set at creation and cannot be changed.`;

    return (
        <>
            <span
                id={tooltipTargetId}
                data-testid="workflow-type-badge"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "3px 10px",
                    backgroundColor: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#3730a3",
                    whiteSpace: "nowrap"
                }}
            >
                {prefix}: {display}
            </span>
            <Tooltip
                target={`#${tooltipTargetId}`}
                showEvent="mouseenter"
                hideEvent="mouseleave"
                hideOnOutsideClick={false}
                position="top"
            >
                <div data-testid="workflow-type-tooltip-content" style={{ padding: "8px 10px", maxWidth: "260px", textAlign: "left" }}>
                    <div style={{ fontWeight: 600, marginBottom: description ? "4px" : 0 }}>{display}</div>
                    {description && (
                        <div style={{ fontSize: "12px", lineHeight: 1.4 }}>{description}</div>
                    )}
                    <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "6px" }}>{lockNote}</div>
                </div>
            </Tooltip>
        </>
    );
}
